import type { PriorityLevel } from "@/lib/priority";

import type { LocalityRef, LocalityStatus } from "@/types/localities";

export type OrderStatus =
  | "new"
  | "in_route"
  | "visited"
  | "confirmed"
  | "rejected"
  | "skipped"
  | "archived";

export type DeliveryType = "delivery" | "pickup";

/**
 * A single scannable parcel attached to an order. Read-only on the dispatcher
 * web surface — parcels carry the barcode/QR codes verified at the depot
 * load-check. An order may have multiple parcels.
 */
export interface Parcel {
  id: number;
  /** Scannable barcode/QR value, or null if not yet assigned a code. */
  code: string | null;
  description: string;
  /** Decimal string from DRF DecimalField, or null. */
  weight_kg: string | null;
}

/**
 * A single parcel item supplied when creating an order with explicit
 * per-parcel rows. Mirrors the backend's nested `parcels` write field on
 * `OrderCreateSerializer` (TASK-3801). All fields are optional — an empty
 * code lets the backend leave it unassigned / auto-generate.
 */
export interface ParcelInput {
  /** Optional barcode/QR code; blank/omitted leaves it unassigned. */
  code?: string;
  description?: string;
  /** Decimal string or number; the backend accepts either. */
  weight_kg?: string | number;
}

/**
 * Write payload for the nested parcels endpoints
 * (POST/PATCH /api/v1/orders/{id}/parcels/). Same shape as {@link ParcelInput}.
 */
export type ParcelWritePayload = ParcelInput;

/**
 * Read-only recipient feedback attached to an order, surfaced by the order
 * detail serializer (TASK-3813). Present only when the recipient left a rating;
 * the order's `feedback` field is `null` otherwise.
 */
export interface OrderFeedback {
  /** Star rating, 1–5. */
  rating: number;
  /** Free-text comment; may be an empty string when none was left. */
  comment: string;
  /** ISO 8601 timestamp of when the feedback was submitted. */
  created_at: string;
}

export type ConfirmationMode = "photo" | "signature" | "both";

/**
 * Resolved proof-of-delivery method exposed by the order API.
 * `none` means no confirmation is required for this order.
 */
export type PodMethod = "photo" | "signature" | "both" | "none";

export interface Order {
  id: number;
  /**
   * Optional external order number. The backend model is
   * `CharField(blank=True, null=True)` and `_normalize_external_id` collapses a
   * blank value to `None`, so the API genuinely serialises this as `null` for
   * every order without an external id (BUG-409). Never type it as plain
   * `string` — that lie is what let three rounds of the zod
   * "Expected string, received null" bug slip past `tsc`.
   */
  external_id: string | null;
  /**
   * Short human-readable name. `CharField(blank=True)` on the backend, so an order created
   * without one serialises as `""` — and the import has always accepted a «Название» column.
   * Typed nullable for the same reason as `external_id` above: do not narrow it to `string`.
   */
  title?: string | null;
  order_type: string;
  delivery_type: DeliveryType;
  address: string;
  /**
   * Latitude. Decimal string from DRF `DecimalField(null=True, blank=True)`, so
   * it is genuinely `null` for any order that has not been geocoded yet or
   * whose geocoding FAILED (BUG-409). Never type it as plain `string` — that
   * lie is what hid the zod null-rejection bug from `tsc`. Parse with
   * `parseFloat(lat ?? "")` and guard `isFinite` before using it as a coord.
   */
  lat: string | null;
  /** Longitude. Nullable for the same reason as {@link Order.lat} (BUG-409). */
  lon: string | null;
  /**
   * Read-only. `true` only when geocoding was ATTEMPTED and failed (empty
   * result) — the order has no coordinates and won't render on the map. A
   * freshly-created order still being geocoded async is `false` with null
   * lat/lon; a successful geocode is `false` with lat/lon set. Never badge on
   * `lat == null` alone — use this flag.
   */
  geocode_failed: boolean;
  /**
   * Sprint 228 — the locality (НП) this order is bound to. `null` when
   * `locality_status` is `not_found` or `""`. On `ambiguous` it is the NEAREST
   * candidate. Optional only so fixtures and cached payloads from before the
   * deploy still type-check; an absent value reads as "not resolved yet".
   */
  locality?: LocalityRef | null;
  /** Sprint 228 — how {@link Order.locality} was decided. */
  locality_status?: LocalityStatus;
  /** Sprint 228 — metres from the point to the locality, or null. */
  locality_distance_m?: number | null;
  /**
   * Why geocoding failed — a stable `GEOCODING_*` registry code (TASK-4744), or
   * `null`/absent. Null is normal, not anomalous: orders that failed before
   * TASK-4744 shipped were deliberately not backfilled with an invented reason.
   * Optional so fixtures and older backends still type-check.
   */
  geocode_error_code?: string | null;
  /** The provider's own one-line explanation. Never a Python repr. */
  geocode_error_message?: string | null;
  /** Decimal string from DRF DecimalField */
  weight_kg: string;
  /** Decimal string from DRF DecimalField */
  volume_m3: string;
  /** Pallet count. Decimal string from DRF DecimalField (TASK-3818). */
  pallets: string;
  service_time_min: number;
  /** Three-level priority (TASK-4833) — was an integer 1–99. */
  priority: PriorityLevel;
  strict_time_window: boolean;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  notes: string;
  time_window_start: string | null;
  time_window_end: string | null;
  delivery_date: string;
  status: OrderStatus;
  route_id: number | null;
  /**
   * Status of the route this order is on, or null when it is on none (BUG-624).
   *
   * Needed to tell whether a constraint change requires a rebuild to take effect: a
   * `planned` or `active` route was computed with the order's OLD values, while a completed
   * or cancelled one is history and warning about it would be noise.
   */
  route_status?:
    | "planned"
    | "active"
    | "completed"
    | "archived"
    | "cancelled"
    | null;
  /** JSON array of strings from backend */
  tags: string[];
  /** Decimal string from DRF DecimalField */
  item_cost: string;
  /** Arbitrary key-value pairs from custom field definitions */
  custom_fields?: Record<string, string>;
  /** Read-only list of scannable parcels (barcode/QR codes) on this order. */
  parcels: Parcel[];
  /** Per-order confirmation override; null means inherit tenant default */
  require_confirmation: boolean | null;
  /** Per-order confirmation mode override; null means inherit tenant default */
  confirmation_mode: ConfirmationMode | null;
  /** Per-order confirmation radius in metres; null means no override. */
  confirmation_radius_m: number | null;
  /** Effective value after applying tenant default fallback */
  effective_require_confirmation: boolean;
  /** Effective mode after applying tenant default fallback */
  effective_confirmation_mode: ConfirmationMode;
  /**
   * Resolved proof-of-delivery method for this order.
   * `none` when confirmation is not required, otherwise the order override
   * or tenant default mode. Returned by the order detail/list/create API.
   */
  pod_method: PodMethod;
  /** Human-readable POD method from the backend (fallback display string). */
  pod_method_display: string;
  created_at: string;
  updated_at: string;
  /**
   * Recipient feedback for this order, or `null` when none was left
   * (TASK-3813). Optional for backward compatibility with payloads predating
   * the serializer field.
   */
  feedback?: OrderFeedback | null;
}

/**
 * Whole-queryset Sum aggregates the Orders list endpoint attaches for the map
 * KPI capsule (TASK-4424). Computed over the tenant-scoped, filter-respecting
 * queryset BEFORE pagination; the `status` filter IS applied, so a status
 * selection narrows the totals. Decimal sums arrive as fixed-precision STRINGS
 * (e.g. `"10.000"`) from DRF `DecimalField`; parse with `Number(...)` and guard
 * `NaN → 0` before rendering. Orders have no distance/duration/stops concept.
 */
export interface OrderSums {
  /** Sum of weight_kg, decimal string (kg). */
  weight: string;
  /** Sum of volume_m3, decimal string (m³). */
  volume: string;
  /** Sum of pallets, decimal string. */
  pallets: string;
}

/**
 * Whole-queryset status counts the Orders list endpoint attaches (TASK-4425),
 * keyed by the 7 order-status lifecycle values. Computed before pagination and,
 * mirroring Routes, EXCLUDING the `status` param itself, so a selected status
 * still shows all-status totals. Read defensively (`?? 0`) for the loading
 * state / older cached payloads.
 */
export type OrderStatusCounts = Record<OrderStatus, number>;

export interface PaginatedOrders {
  count: number;
  next: string | null;
  previous: string | null;
  results: Order[];
  /** Whole-queryset per-status counts (TASK-4425). Optional for older payloads. */
  status_counts?: OrderStatusCounts;
  /** Whole-queryset Sum aggregates for the KPI capsule (TASK-4424). */
  sums?: OrderSums;
}

// ---------------------------------------------------------------------------
// Unified Orders list (TASK-4702 / TASK-4704)
//
// `GET /api/v1/orders/unified/` merges regular orders (`Order`) and permanent
// orders (`OrderTemplate`) into ONE paginated list, so a dispatcher can select a
// mix of both and build a single route.
//
// The two sources are modelled as a DISCRIMINATED UNION on `kind` rather than
// one wide optional-field interface. That is deliberate: a template row genuinely
// has NO delivery date and NO lifecycle status, and `tsc` must force every
// consumer to handle that case instead of silently rendering `undefined` in a
// date or status cell.
// ---------------------------------------------------------------------------

/** Source discriminator for a unified row. */
export type UnifiedRowKind = "order" | "template";

/**
 * Fields present on BOTH sources of the unified list.
 *
 * NOTE on `row_id`: the numeric `id` **collides** across the two sources — order
 * 12 and template 12 are different objects that both exist. `row_id` is the
 * composite `"order:12"` / `"template:12"` string and is the ONLY safe selection
 * key. Never key selection, a React `key`, or a lookup Map on `id`.
 */
interface UnifiedRowBase {
  /** Composite selection key: `"order:<id>"` / `"template:<id>"`. Unique. */
  row_id: string;
  /** Numeric pk WITHIN its own source. NOT unique across the union. */
  id: number;
  /** ISO 8601 timestamp — the only meaningful column both models share. */
  created_at: string;
  /** Display title: order external_id / template name. */
  title: string;
  address: string;
  customer_name: string;
  customer_phone: string;
  /** Latitude. DRF decimal string, or null when not geocoded. */
  lat: string | null;
  /** Longitude. DRF decimal string, or null when not geocoded. */
  lon: string | null;
  /** DRF decimal string. */
  weight_kg: string;
  /** DRF decimal string. */
  volume_m3: string;
  /** DRF decimal string. */
  pallets: string;
  /** Three-level priority (TASK-4833), real on BOTH kinds. */
  priority: PriorityLevel;
  time_window_start: string | null;
  time_window_end: string | null;
  /** Order external id, or the template's external id (may be an empty string). */
  external_id: string | null;
  tags: string[];
  /**
   * Direction of goods — `delivery` or `pickup`. Real on BOTH kinds
   * (`UnifiedOrderRowSerializer.delivery_type`).
   *
   * `pickup` is what the UI calls a RETURN («Возврат»), and it is what drives
   * the amber `↩` marker beside the order number (TASK-4822). Do NOT confuse it
   * with `Order.order_type`, whose choices are `one_time`/`recurring` — that
   * field is a frequency and is not part of the unified row at all.
   *
   * Optional because the field was added to this interface after the endpoint
   * shipped: existing test fixtures omit it, and an absent value simply means
   * "not a return".
   */
  delivery_type?: DeliveryType | null;
  /**
   * Whether the time window is STRICT. A real stored boolean on **both** kinds
   * (`UnifiedOrderRowSerializer.strict_time_window`) — never null on the wire.
   *
   * Optional here only because fixtures written before TASK-4826 omit it; an
   * absent value means "unknown", which the bulk tri-state switch treats the
   * same way it treats `null` — excluded from the verdict, NOT `false`.
   */
  strict_time_window?: boolean | null;
  /**
   * Whether the whole service must fit inside the time window.
   *
   * **Real boolean on `order` rows; `null` on every `template` row.**
   * `OrderTemplate` has no such field, so the server sends a typed NULL rather
   * than a fabricated `false` — `null` means NOT APPLICABLE.
   *
   * Reading `null` as `false` is a correctness bug, not a cosmetic one: it
   * folds a permanent order into an "all-off" verdict and renders a setting the
   * row does not have as deliberately switched off (TASK-4826).
   */
  service_in_window?: boolean | null;
  /**
   * Sprint 228 — the row's locality, its status and distance. Real on BOTH
   * kinds. Optional like the other late-added unified fields, so a cached
   * response from before the deploy still renders (no badge).
   */
  locality?: LocalityRef | null;
  locality_status?: LocalityStatus;
  locality_distance_m?: number | null;
}

/** A regular order row: it HAS a delivery date and a lifecycle status. */
export interface UnifiedOrderRow extends UnifiedRowBase {
  kind: "order";
  /** `delivery_date` (YYYY-MM-DD). Always present on an order row. */
  sort_date: string;
  status: OrderStatus;
}

/**
 * A permanent-order (template) row.
 *
 * `sort_date` and `status` are `null` — a permanent order has neither a delivery
 * date nor a lifecycle status. Render both cells as an em-dash; never fabricate
 * a value and never render an empty cell.
 */
export interface UnifiedTemplateRow extends UnifiedRowBase {
  kind: "template";
  sort_date: null;
  status: null;
}

/** One row of the unified Orders list — discriminate on `kind`. */
export type UnifiedRow = UnifiedOrderRow | UnifiedTemplateRow;

/**
 * Whole-list status counts of the unified endpoint: the seven order statuses
 * plus a `template` key carrying the whole-list permanent-order count (templates
 * have no status of their own). Computed BEFORE pagination, so it is identical
 * on every page.
 */
export type UnifiedStatusCounts = OrderStatusCounts & { template: number };

/**
 * Response envelope of `GET /api/v1/orders/filter-counts/` (TASK-4823).
 *
 * The COUNT-ONLY companion of the unified list: it answers "how many rows would
 * this filter set return" without paying for a page of rows, which is what lets
 * the filters panel show «найдено N из M» and «Показать N» for a set the user
 * has not applied yet.
 *
 * `status_counts` is computed with the `status` filter STRIPPED — each entry
 * answers "how many rows would selecting *this* status yield, under the other
 * conditions". That is why the chip counts do NOT move when the user toggles a
 * status: it looks like a bug the first time you see it, and it is not.
 *
 * BUG-505 (landed): the endpoint originally counted `Order` rows only while the
 * list is `/orders/unified/` (orders + permanent orders), so it ignored `?type=`
 * entirely — `type=templates` answered 5 920 over a list of 14. It is now
 * `type`-aware and routed through the unified list's own helpers, so `count`
 * equals what `/unified/` returns for the same params.
 *
 * TWO SEMANTICS THAT LOOK LIKE BUGS AND ARE NOT:
 *
 * 1. `template` sits BESIDE the seven statuses and is NOT part of their sum.
 *    Deriving a total by summing `status_counts` is off by exactly the template
 *    count. Read `count`/`total` off the response; never sum the object.
 * 2. `status_counts` is type-INDEPENDENT (it follows `/unified/`'s BUG-438
 *    rule), so it does not sum to `count` unless `type=all`. Do not assert or
 *    build a UI invariant on it summing.
 *
 * `template` stays OPTIONAL here so the client is correct against an older
 * cached payload too — read it as `?? 0`, never assume it.
 */
export interface OrderFilterCounts {
  /** Rows matching the pending set INCLUDING status → «Показать N» and the N. */
  count: number;
  /**
   * Rows matching every condition EXCEPT status → the M of «найдено N из M».
   * Counted over the same sources as `count`, so under `type=templates` it
   * equals `count` (N из N) rather than an absurd «6 из 5 920».
   */
  total: number;
  /** Per-status counts, computed with `status` stripped. Zeros included. */
  status_counts: OrderStatusCounts & { template?: number };
}

/** Response envelope of `GET /api/v1/orders/unified/`. */
export interface PaginatedUnifiedRows {
  count: number;
  next: string | null;
  previous: string | null;
  results: UnifiedRow[];
  /** Whole-list per-status counts + the `template` count. */
  status_counts?: UnifiedStatusCounts;
  /** Whole-list decimal-string sums spanning BOTH sources. */
  sums?: OrderSums;
}

/**
 * Request body of `POST /api/v1/orders/build_route_mixed/` (TASK-4703).
 *
 * ONE call builds a single route from a selection that may contain regular
 * orders (`order_ids`), permanent orders (`template_ids`), or both. Sending two
 * separate builds for a mixed selection would produce two routes, which is the
 * exact limitation sprint 169 exists to remove.
 *
 * Unlike `POST /v1/routes/build/`, `date` is REQUIRED here: materializing a
 * permanent order needs a concrete `delivery_date`.
 */
export interface BuildRouteMixedPayload {
  /** Pks of the selected regular orders. May be empty when only templates are picked. */
  order_ids: number[];
  /** Pks of the selected permanent orders. May be empty when only orders are picked. */
  template_ids: number[];
  /** Execution date of the route (YYYY-MM-DD). Required. */
  date: string;
  strategy: "manual" | "auto";
  vehicle_id?: number;
  visit_order?: "strict" | "any";
  warehouse_id?: number | null;
  mid_warehouse_id?: number | null;
  end_warehouse_id?: number | null;
}

/**
 * Response of `POST /api/v1/orders/build_route_mixed/` (HTTP 202).
 *
 * `task_id`/`status` are byte-identical to `BuildRouteTaskResponse` from
 * `POST /v1/routes/build/`, so the shared build poller (`buildRouteTaskAtom` →
 * AppShell `useBuildRoutePoller`) drives it untouched. The two id lists are
 * the extra reporting the mixed endpoint adds.
 */
export interface BuildRouteMixedResponse {
  task_id: string;
  status: "pending" | "running" | "completed" | "failed";
  /** Full de-duplicated id list handed to the solver (selected + created). */
  order_ids: number[];
  /**
   * Orders freshly materialized by this call. Every selected permanent order
   * always yields a NEW order (BUG-439): the same permanent order may be used
   * several times a day and appear in different routes, each a real delivery.
   */
  created_order_ids: number[];
}

export type CreateOrderPayload = {
  /** Short human-readable name. Optional — the import has always accepted a «Название» column,
   *  and the manual create form now offers the same field. */
  title?: string;
  external_id: string;
  address: string;
  lat: string | number;
  lon: string | number;
  delivery_date: string;
  order_type?: string;
  delivery_type?: DeliveryType;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  weight_kg?: string | number;
  volume_m3?: string | number;
  /** Pallet count; empty/omitted defaults to 0 on the backend. */
  pallets?: string | number;
  service_time_min?: number;
  /** Three-level priority (TASK-4833). */
  priority?: PriorityLevel;
  strict_time_window?: boolean;
  time_window_start?: string | null;
  time_window_end?: string | null;
  item_cost?: string | number;
  notes?: string;
  tags?: string[];
  custom_fields?: Record<string, string>;
  /** null = inherit tenant default */
  require_confirmation?: boolean | null;
  /** null = inherit tenant default */
  confirmation_mode?: ConfirmationMode | null;
  /** Per-order confirmation radius in metres; null clears the override. */
  confirmation_radius_m?: number | null;
  /** Whether service time must be performed inside the time window. */
  service_in_window?: boolean;
  /**
   * Explicit per-parcel rows created together with the order (TASK-3801).
   * When present this takes precedence over {@link package_count}.
   */
  parcels?: ParcelInput[];
  /**
   * Number of packages to auto-generate barcodes for (TASK-3802). Only
   * honoured by the backend when the tenant's `barcode_generation_mode` is
   * `order_number`. Ignored when `parcels` is provided.
   */
  package_count?: number;
};

export type UpdateOrderPayload = Partial<CreateOrderPayload> & {
  /** Toggle the strict time-window constraint (bulk-editable). */
  strict_time_window?: boolean;
  /**
   * Sprint 228 — an id sets that locality as `manual`; `null` clears the manual
   * choice and re-resolves automatically on the server.
   */
  locality_id?: number | null;
};

/**
 * Body for POST /api/v1/orders/{id}/transition/. The backend endpoint is a
 * `ChoiceField` on the target STATUS value (validated against the order state
 * machine `OrderStatusMachine.TRANSITIONS`), NOT a named transition verb.
 */
export interface TransitionOrderPayload {
  status: OrderStatus;
}

export interface ImportOrdersError {
  row: number;
  detail: string;
}

export interface ImportOrdersResult {
  created: number;
  /** Optional: the backend currently returns only `created` + `errors`. */
  updated?: number;
  errors: ImportOrdersError[];
  /**
   * BUG-410: uuid4 stamped on `Order.import_batch_id` for every order this
   * import created. The key for {@link GeocodingProgress} polling. Optional so
   * older backends (and existing test fixtures) still type-check.
   */
  import_batch_id?: string;
  /**
   * BUG-410: how many of the created orders were queued for asynchronous
   * geocoding (the rows that carried no lat/lon) — the progress DENOMINATOR.
   * `0` (or absent) means nothing is pending and NO progress banner is shown.
   */
  geocoding_total?: number;
  /**
   * Every header of the uploaded file the importer could NOT map to a column,
   * verbatim and de-duplicated. Always present — `[]` when every header was
   * understood (BUG-589).
   *
   * An unmapped column is a non-fatal WARNING, not a rejection: the recognised
   * columns still import. But the import is NOT clean, and the client must say
   * so — this is exactly how the owner's file, whose label column was headed
   * «Название», imported 20 rows with an empty `name` and reported success. The
   * data in an ignored column is simply gone.
   *
   * Optional in TypeScript only so existing fixtures and an older backend still
   * type-check; the live contract always sends it.
   */
  ignored_columns?: string[];
}

/**
 * Response of `GET /v1/orders/geocoding-progress/?batch=<uuid>` (BUG-410).
 *
 * Scoped to one import batch AND the caller's tenant, so it counts exactly the
 * orders of that import — never hand-created orders, public-API orders or a
 * concurrent import's leftovers.
 *
 * `pending` is NOT guaranteed to reach 0: `geocode_order` has no retry policy
 * (BUG-415), so a worker restart or a raising provider leaves an order with no
 * coordinates AND `geocode_failed = false`, i.e. permanently pending. The
 * poller therefore enforces its own no-progress timeout instead of waiting for
 * `status === "completed"`.
 */
/**
 * The geocoding failure codes `apps/core/error_codes.py` can store on an order
 * (TASK-4744). Every member has a `orders.geocodeErrors.*` entry in BOTH locale
 * files; {@link isKnownGeocodeErrorCode} is what keeps an unrecognised code off
 * the screen as a raw key path.
 */
export const GEOCODE_ERROR_CODES = [
  "GEOCODING_ADDRESS_NOT_FOUND",
  "GEOCODING_PROVIDER_TIMEOUT",
  "GEOCODING_PROVIDER_AUTH_FAILED",
  "GEOCODING_RATE_LIMITED",
  "GEOCODING_RETRIES_EXHAUSTED",
] as const;

export type GeocodeErrorCode = (typeof GEOCODE_ERROR_CODES)[number];

/**
 * True when `code` has a translated message in this bundle.
 *
 * A backend newer than the deployed frontend can emit a code this bundle has
 * never heard of; that must render readable prose, never `orders.geocodeErrors.X`.
 */
export function isKnownGeocodeErrorCode(
  code: string,
): code is GeocodeErrorCode {
  return (GEOCODE_ERROR_CODES as readonly string[]).includes(code);
}

/**
 * One failed order from the progress endpoint's `failed_items` (TASK-4744).
 *
 * `code` and `message` are BOTH nullable: rows that failed before TASK-4744
 * shipped were deliberately not backfilled, so "we don't know why" is a real,
 * honest state the UI has to render.
 */
export interface GeocodingFailureItem {
  order_id: number;
  /** The order's title; `null` when the import row carried none. */
  order_title: string | null;
  /** The address that could not be resolved. Always present. */
  address: string;
  /** Stable `GEOCODING_*` registry code, or `null` for a pre-TASK-4744 row. */
  code: string | null;
  /** The provider's own one-line explanation. Never a Python repr. */
  message: string | null;
}

export interface GeocodingProgress {
  /** Orders in the batch that actually needed geocoding (the denominator). */
  total: number;
  /** Still awaiting coordinates and not marked failed. */
  pending: number;
  /** Coordinates resolved by the geocoder. */
  done: number;
  /** `geocode_failed = true` — the provider returned no match. */
  failed: number;
  /**
   * The failed orders themselves, capped at 20 by the backend (TASK-4744).
   * Optional so a frontend deployed against an older backend still type-checks.
   */
  failed_items?: GeocodingFailureItem[];
  /** True when `failed` exceeds the itemized list, i.e. names were omitted. */
  failed_truncated?: boolean;
  /** `round(100 * (done + failed) / total)`; 100 when `total` is 0. */
  progress_pct: number;
  /** `"completed"` as soon as `pending === 0`. */
  status: "running" | "completed";
}

// ---------------------------------------------------------------------------
// Bulk delete (TASK-4980 / TASK-4981, BUG-577)
// ---------------------------------------------------------------------------

/**
 * One per-item failure of a bulk delete: the echoed id plus a registry code.
 *
 * An unknown id and an id belonging to another tenant BOTH report `NOT_FOUND` —
 * that is deliberate on the backend, so the endpoint cannot become a
 * cross-tenant existence oracle. Do not build UI that distinguishes them.
 */
export interface BulkDeleteFailure {
  id: number;
  code: string;
}

/**
 * Synchronous (HTTP 200) outcome of `POST /api/v1/orders/bulk-delete/`.
 *
 * Every id sent back appears in exactly ONE of the three lists. `skipped` holds
 * ids that were already archived — the per-item form of the 409
 * `ORDER_ALREADY_ARCHIVED` the single-object DELETE raises, which is why the
 * request itself is a 200 and not an error (BUG-519 semantics preserved).
 */
export interface BulkDeleteOrdersResult {
  archived: number[];
  skipped: number[];
  failed: BulkDeleteFailure[];
}

/** Asynchronous (HTTP 202) outcome — the batch was enqueued, nothing archived yet. */
export interface BulkDeleteAccepted {
  task_id: string;
  accepted: number;
}

/**
 * The discriminated result of a bulk delete.
 *
 * `status` is synthesised client-side from the HTTP status code so a caller
 * CANNOT read `archived` off a 202 without a type error — above
 * `BULK_DELETE_SYNC_MAX_IDS` ids the backend answers 202 and nothing has been
 * archived yet, so treating the two shapes interchangeably would report work
 * that has not happened.
 */
export type BulkDeleteOrdersResponse =
  | ({ status: "completed" } & BulkDeleteOrdersResult)
  | ({ status: "accepted" } & BulkDeleteAccepted);

/** Poll result for an enqueued bulk delete (`GET .../bulk-delete/{task_id}/status/`). */
export interface BulkDeleteTaskStatus {
  state: "PENDING" | "STARTED" | "SUCCESS" | "FAILURE" | string;
  result: BulkDeleteOrdersResult | null;
}
